import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { FormControl } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { ICollaboratorViewModel, IProfileViewModel } from 'viewModels';
import * as yup from 'yup';
import { useTheme } from 'styled-components';
import { ReactComponent as EditPen } from '../../../assets/icons/editPen.svg';
import { ReactComponent as ExpandArrowIcon } from '../../../assets/icons/iconExpandArrow.svg';
import { useDepartments, useProfile } from '../../../context';
import { useBranch } from '../../../context/branch';
import { CompanyController } from '../../../controller/CompanyController';
import useClickOutside from '../../../hooks/useClickOutside';
import { ProfileType } from '../../../types/enums';
import { Button } from '../../Buttons/Button';
import { Input } from '../../Inputs/Input';
import { SelectControled } from '../../SelectControled';
import { Form, Header } from './styles';
import { SelectProfileCollaborator } from '../../SelectProfileCollaborator';

interface ModalEditCollaboratorProps {
  isOpen: boolean;
  currentCollaborator: ICollaboratorViewModel;
  onRequestClose: () => void;
  onRequestUpdateCollaborator: () => void;
  runningTutorial?: boolean;
}

export function ModalEditCollaborator({
  currentCollaborator,
  isOpen,
  onRequestClose,
  onRequestUpdateCollaborator,
  runningTutorial,
}: ModalEditCollaboratorProps) {
  const theme = useTheme();
  const { profiles } = useProfile();
  const { branches } = useBranch();
  const { departments } = useDepartments();
  const [searchProfile, setSearchProfile] = useState<string>('');
  const [, setSearchProfileId] = useState<string>('');
  const [typeProfileSearchSelected] = useState<ProfileType>();
  const [departmentId, setDepartmentId] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [profileId, setProfileId] = useState<string>('');
  const [viewProfiles, setViewProfiles] = useState(false);
  const defaultValues = {
    name: currentCollaborator.firstName,
    branch: currentCollaborator.branchId,
    department: currentCollaborator.departmentId,
    typeProfile: currentCollaborator.profileId,
  };

  const handleProfileSearch = useCallback(
    (value: string) => {
      if (value === '' && typeProfileSearchSelected === undefined) return;

      if (typeProfileSearchSelected === undefined) {
        setProfileResultItem(
          profiles.filter((p) => p.name?.toLowerCase().startsWith(value?.toLowerCase())),
        );
      } else {
        setProfileResultItem(
          profiles.filter(
            (p) =>
              p.name?.toLowerCase().startsWith(value?.toLowerCase()) &&
              p.type === Number(typeProfileSearchSelected),
          ),
        );
      }
    },
    [profiles, typeProfileSearchSelected],
  );

  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(() => {
    setViewProfiles(false);
  }, ref);

  const [, setProfileResultItem] = useState<IProfileViewModel[]>([]);

  const collaboratorSchema = yup.object().shape({
    name: yup.string().required('O campo nome é obrigatório!'),
    typeProfile: yup.string().required('O campo perfil é obrigatório'),
    branch: yup.string().required('O campo filial é obrigatório'),
    department: yup.string().required('O campo departamento é obrigatório'),
  });

  const {
    formState: { errors },
    getValues,
    control,
    setValue,
    handleSubmit,
  } = useForm({
    reValidateMode: 'onChange',
    resolver: yupResolver(collaboratorSchema),
    defaultValues,
  });

  const handleUpdateCurrentCollaborator = () => {
    const updatedCollaborator: ICollaboratorViewModel = {
      id: currentCollaborator.id,
      firstName: getValues('name'),
      companyId: currentCollaborator.companyId,
      email: currentCollaborator.email,
      departmentId: departmentId || currentCollaborator.departmentId,
      profileId: profileId || currentCollaborator.profileId,
      branchId: branchId || currentCollaborator.branchId,
    };
    if (!runningTutorial) {
      CompanyController.updateCollaborator(updatedCollaborator).then(() => {
        onRequestClose();
        setSearchProfile('');
        setSearchProfileId('');
      });
    } else {
      onRequestClose();
      setSearchProfile('');
      setSearchProfileId('');
    }
    onRequestUpdateCollaborator();
  };

  const MenuProps = {
    PaperProps: {
      style: {
        backgroundColor: theme.colors.background_white,
        color: theme.colors.secondary,
      },
    },
  };

  useEffect(() => {
    if (currentCollaborator) {
      setValue('name', currentCollaborator.firstName);
      setValue('typeProfile', currentCollaborator.profileId);
      setValue('branch', currentCollaborator.branchId);
      setValue('department', currentCollaborator.departmentId);
      setDepartmentId(currentCollaborator.departmentId || '');
      setBranchId(currentCollaborator.branchId || '');
      setProfileId(currentCollaborator.profileId || '');
    }
  }, [currentCollaborator, setValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleProfileSearch(searchProfile);
    }, 1000);
    return () => clearTimeout(timer);
  }, [handleProfileSearch, searchProfile]);
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-edit-modal-content">
      <Header>
        <div className="subHeader">
          <EditPen />
          <h1>Edição</h1>
        </div>
        <div className="close-div">
          <button type="button" onClick={onRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <Form active={viewProfiles}>
        <h2>E-mail</h2>
        <p>{currentCollaborator.email}</p>
        <div className="containerName">
          <Input
            name="name"
            variant="standard"
            label="Nome"
            control={control}
            value={getValues('name')}
            errorText={errors.name?.message}
          />
        </div>
        <div className="TypeProfile">
          <div className="divGrid">
            <div className="contentDep">
              <p>Departamento</p>
              <div className="divDropDownList">
                <FormControl fullWidth>
                  <SelectControled
                    name="department"
                    classes={{ select: 'select-style' }}
                    defaultValue={currentCollaborator.departmentId}
                    MenuProps={MenuProps}
                    value={departmentId}
                    displayEmpty
                    IconComponent={ExpandArrowIcon}
                    onChange={(e) => setDepartmentId(e.target.value as string)}
                    control={control}
                    placeholderTitle="Selecione"
                    itens={departments
                      .filter((d) => d.isActive)
                      .map((department) => ({ id: department.id, value: department.name }))}
                  />
                </FormControl>
              </div>
            </div>

            <div className="contentBranch">
              <p>Filial</p>
              <div className="divDropDownList">
                <FormControl fullWidth>
                  <SelectControled
                    name="branch"
                    classes={{ select: 'select-style' }}
                    defaultValue={currentCollaborator.branchId}
                    MenuProps={MenuProps}
                    value={branchId}
                    control={control}
                    displayEmpty
                    IconComponent={ExpandArrowIcon}
                    onChange={(e) => {
                      setBranchId(e.target.value as string);
                      setValue('branch', e.target.value as string);
                    }}
                    placeholderTitle="Selecione"
                    itens={branches.map((branch) => ({ id: branch.id, value: branch.name }))}
                  />
                </FormControl>
              </div>
            </div>

            <div className="contentProfile">
              <div className="profile">
                <p>Perfil</p>
                <div className="divDropDownList">
                  <FormControl fullWidth>
                    <SelectProfileCollaborator
                      name="typeProfile"
                      control={control}
                      defaultValue={currentCollaborator.profileId}
                      value={profileId}
                      onChange={(e) => {
                        setValue('typeProfile', e.target.value as string);
                        setProfileId(e.target.value as string);
                      }}
                      placeholderTitle="Selecione"
                      operators={profiles
                        .filter((p) => p.type === ProfileType.OPERATOR)
                        .map((profile) => ({ id: profile.id, name: profile.name }))}
                      approvers={profiles
                        .filter((p) => p.type === ProfileType.APPROVER)
                        .map((profile) => ({ id: profile.id, name: profile.name }))}
                      requesters={profiles
                        .filter((p) => p.type === ProfileType.REQUESTER)
                        .map((profile) => ({ id: profile.id, name: profile.name }))}
                      financers={profiles
                        .filter((p) => p.type === ProfileType.FINANCIAL)
                        .map((profile) => ({ id: profile.id, name: profile.name }))}
                    />
                  </FormControl>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="buttonModal">
          <Button
            text="Salvar alterações"
            dimensions={{ width: '13rem', height: '2.25rem' }}
            onClick={handleSubmit(handleUpdateCurrentCollaborator)}
          />
        </div>
      </Form>
    </Modal>
  );
}
